import { useWidgetContext } from 'widgets/WidgetContext';
import styleConstants from 'styles/variables.module.scss';
import {
    useDispatchUpdateWidgetConfiguration,
    useWidgetConfigState,
} from 'widgets/store/configuration/hooks';
import { DockingConfig } from '@instinet-analytics/semantic-ui-components';
import { WidgetConfigWithHideableToolbar } from 'widgets/WidgetToolbarShowHidePanel/WidgetConfigWithHideableToolbar';

const padding = 2 * parseInt(styleConstants.spaceAround);
const toolbarHeight = parseInt(styleConstants.panelHeaderHeight);
const tallerToolbarHeight = parseInt(styleConstants.tallerPanelHeaderHeight);
const borderWidth = 2 * parseInt(styleConstants.layoutBorderWidth);

type Options<TConfig extends DockingConfig & WidgetConfigWithHideableToolbar> =
    {
        showDockablePanel?: boolean;
        tallPanelHeader?: boolean;
        initialConfig: TConfig;
    };

export function useWidgetWithDockablePanelDimensions<
    TConfig extends DockingConfig & WidgetConfigWithHideableToolbar,
>(options: Options<TConfig>) {
    const { showDockablePanel, tallPanelHeader, initialConfig } = options;
    const { height, width } = useWidgetContext();
    const config = useWidgetConfigState<TConfig>();
    const dispatchConfigChanged =
        useDispatchUpdateWidgetConfiguration<TConfig>();

    const {
        hideToolbar,
        dockPosition: configDockPosition,
        dockPercentage: configDockPercent,
    } = config;

    const separatorHeight = 4;
    const panelPadding = 8;

    const percentage = configDockPercent ?? initialConfig.dockPercentage!;
    const position = configDockPosition ?? initialConfig.dockPosition!;
    const vertical = position === 'Top' || position === 'Bottom';
    const dockablePanelFirst = position === 'Top' || position === 'Left';

    const toolbarConsumedHeight = hideToolbar
        ? 0
        : tallPanelHeader
        ? tallerToolbarHeight
        : toolbarHeight;
    const contentHeight =
        height - padding - borderWidth - toolbarConsumedHeight;

    const dockablePanelHeight = vertical
        ? (contentHeight * percentage) / 100
        : contentHeight;

    const mainPanelHeight =
        vertical && showDockablePanel
            ? contentHeight - dockablePanelHeight - separatorHeight
            : contentHeight;

    const panelWidth =
        !vertical && showDockablePanel
            ? width - padding - borderWidth - separatorHeight
            : width - padding - borderWidth;
    const dockablePanelWidth = vertical
        ? panelWidth
        : (panelWidth * percentage) / 100;
    const mainPanelWidth =
        !vertical && showDockablePanel
            ? panelWidth - dockablePanelWidth - panelPadding
            : panelWidth;

    const handlePercentageChange = (value: number) => {
        const delta: Partial<TConfig> = {};
        delta.dockPercentage = value;
        dispatchConfigChanged(delta);
    };

    return {
        contentHeight,
        percentage,
        position,
        dockablePanelHeight,
        dockablePanelWidth,
        dockablePanelFirst,
        mainPanelWidth,
        mainPanelHeight,
        handlePercentageChange,
    };
}
